// react components
import React, { useState } from 'react'

// Define the props for the component
interface FormDataDisplayProps {
    extra_data_json?: any
    form_fields_mapping?: { [key: string]: string }
    package_option_items?: any[]
    currency_symbol?: string
    deviceType: string
    containerClassName?: string
    fieldClassName?: string
    renderAs?: 'span' | 'div'
}

// main
export const FormDataDisplaySite: React.FC<FormDataDisplayProps> = ({
    extra_data_json,
    form_fields_mapping,
    package_option_items,
    currency_symbol,
    deviceType,
    containerClassName = 'form-data',
    fieldClassName = 'form-field',
    renderAs = 'span',
}) => {
    const [isExpanded, setIsExpanded] = useState(false)

    const FieldElement = renderAs

    // Collect all form fields into an array
    const formFields: JSX.Element[] = []

    // Add package options first if available
    if (package_option_items && package_option_items.length > 0) {
        package_option_items.forEach((item, index) => {
            const isFree = (item.product?.unit_price || 0) === 0
            const optionLabel = isFree ? 'Options gratuites:' : 'Options:'

            formFields.push(
                <FieldElement key={`package-${item.id || index}`} className={`${fieldClassName} ${deviceType} package-option`}>
                    <strong>{optionLabel}</strong>{' '}
                    {item.product?.name}: {item.qty} x {item.product?.unit_price || 0} {currency_symbol}
                </FieldElement>
            )
        })
    }

    // Skip if no extra_data_json and no package options
    if ((!extra_data_json || Object.keys(extra_data_json).length === 0) && formFields.length === 0) {
        return null
    }

    // Handle nested form structure (for products with form key)
    if (extra_data_json && extra_data_json.form && Object.keys(extra_data_json.form).length > 0) {
        Object.entries(extra_data_json.form).forEach(([fieldName, fieldData]) => {
            if (fieldData && typeof fieldData === 'object' && 'label' in fieldData && 'value' in fieldData) {
                const { label, value } = fieldData as { label: string, value: any }
                let formattedValue: any = value

                // Handle different value types
                if (value === null || value === undefined) {
                    return // Skip null/undefined values
                } else if (typeof value === 'string' || typeof value === 'number') {
                    // Primitives - use as is
                    formattedValue = value
                } else if (value === true) {
                    formattedValue = 'Oui'
                } else if (value === false) {
                    formattedValue = 'Non'
                } else if (Array.isArray(value)) {
                    // Handle arrays
                    if (value.length === 0) {
                        return
                    }
                    // Check if array contains objects with product info
                    const firstItem = value[0]
                    if (typeof firstItem === 'object' && firstItem !== null) {
                        // Try to extract values from array of objects
                        const values = value.map(item =>
                            item.value || item.name || item.title || item.label || item.id
                        ).filter(Boolean)
                        formattedValue = values.length > 0 ? values.join(', ') : null
                        if (!formattedValue) return
                    } else {
                        // Array of primitives
                        formattedValue = value.join(', ')
                    }
                } else if (typeof value === 'object') {
                    // Try to extract meaningful value from object
                    if ('value' in value) {
                        formattedValue = value.value
                    } else if ('label' in value) {
                        formattedValue = value.label
                    } else if ('name' in value) {
                        formattedValue = value.name
                    } else if ('title' in value) {
                        formattedValue = value.title
                    } else if ('product_name' in value) {
                        formattedValue = value.product_name
                    } else if ('absolute_site_url' in value) {
                        // This is likely a product object, try common product name fields
                        formattedValue = value.name || value.title || value.product_name ||
                            value.display_name || value.label ||
                            `Product #${value.id}`
                    } else if ('id' in value) {
                        formattedValue = value.id
                    } else {
                        // If still an object, skip it
                        return
                    }
                }

                formFields.push(
                    <FieldElement key={fieldName} className={`${fieldClassName} ${deviceType}`}>
                        <strong>{label}:</strong>{' '}
                        {formattedValue}
                    </FieldElement>
                )
            }
        })
    }

    // Handle flat structure (for tickets with form_fields_mapping)
    if (extra_data_json && !extra_data_json.form) {
        Object.entries(extra_data_json).forEach(([fieldName, fieldValue]) => {
            // Skip empty values or objects
            if (!fieldValue || typeof fieldValue === 'object') return

            // Get the label from mapping, or format the field name
            const label = form_fields_mapping?.[fieldName] || fieldName
                .replace(/_/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())

            // Format the value
            let formattedValue: string | number
            if (fieldValue === true) formattedValue = 'Oui'
            else if (fieldValue === false) formattedValue = 'Non'
            else formattedValue = String(fieldValue)

            formFields.push(
                <FieldElement key={fieldName} className={`${fieldClassName} ${deviceType}`}>
                    <strong>{label}:</strong>{' '}
                    {formattedValue}
                </FieldElement>
            )
        })
    }

    // If no fields, return null
    if (formFields.length === 0) {
        return null
    }

    // Determine which fields to show
    const fieldsToShow = isExpanded ? formFields : formFields.slice(0, 1)
    const hasMoreFields = formFields.length > 1

    return (
        <div className={`${containerClassName} ${deviceType} ${!isExpanded ? 'collapsed' : 'expanded'}`}>
            {fieldsToShow}
            {hasMoreFields && (
                <button
                    type="button"
                    className={`${containerClassName}-toggle ${deviceType}`}
                    onClick={() => setIsExpanded(!isExpanded)}
                >
                    {isExpanded ? 'Voir moins' : 'Voir plus'}
                </button>
            )}
        </div>
    )
}
